<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="compact-header" elevation="3">
          <v-card-title class="d-flex justify-space-between align-center">
            <div>
              <h2 class="mb-1">Teams Management</h2>
              <p class="text-caption text-grey-darken-1 mb-0">
                Manage partner teams
              </p>
            </div>
            <div class="d-flex align-center filter-container">
              <v-select
                v-model="selectedYear"
                :items="yearOptions"
                label="Year"
                variant="outlined"
                density="compact"
                class="filter-select"
                hide-details
                @update:model-value="onFilterChange"
              />

              <v-select
                v-model="selectedMonth"
                :items="monthOptions"
                label="Month"
                variant="outlined"
                density="compact"
                class="filter-select"
                hide-details
                @update:model-value="onMonthChange"
              />

              <v-select
                v-model="selectedFilter"
                :items="statusOptions"
                label="Status"
                variant="outlined"
                density="compact"
                class="filter-select"
                hide-details
              />
            </div>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-2">
      <v-col cols="12">
        <v-card elevation="2" class="table-card">
          <v-data-table
            :headers="headers"
            :items="filteredTeams"
            :items-per-page="itemsPerPage"
            :loading="loading"
            :page="currentPage"
            :height="CommonVuetifyObj.height - 260"
            class="modern-data-table"
            density="compact"
            hover
            fixed-header
            hide-default-footer
          >
            <template v-slot:[`item.organization_display_id`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small">mdi-id-card</v-icon>
                <div class="ml-2">{{ item.organization_display_id }}</div>
              </div>
            </template>

            <template v-slot:[`item.organization_name`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small"
                  >mdi-account-group</v-icon
                >
                <div class="ml-2">{{ item.organization_display_id }}</div>
              </div>
            </template>

            <template v-slot:[`item.organization_created_by`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small"
                  >mdi-email-outline</v-icon
                >
                <div class="ml-2">{{ item.organization_created_by }}</div>
              </div>
            </template>

            <template v-slot:[`item.organization_billing_type`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small"
                  >mdi-credit-card-outline</v-icon
                >
                <div class="ml-2">
                  {{
                    item.organization_billing_type
                      ? item.organization_billing_type.replaceAll("_", " ")
                      : "-"
                  }}
                </div>
              </div>
            </template>

            <template v-slot:[`item.billing_rate`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small">mdi-cash</v-icon>
                <div class="ml-2">
                  ₹
                  {{
                    item.billing_rate
                      ? item.billing_rate.toLocaleString()
                      : "N/A"
                  }}
                </div>
              </div>
            </template>

            <template v-slot:[`item.organization_type`]="{ item }">
              <v-chip
                :color="
                  item.organization_type === 'LIVE' ? 'success' : 'warning'
                "
                size="small"
                variant="flat"
              >
                {{ item.organization_type }}
              </v-chip>
            </template>

            <template v-slot:[`item.organization_created_on`]="{ item }">
              <div class="d-flex align-center">
                <v-icon class="icon-color" size="small"
                  >mdi-clock-outline</v-icon
                >
                <div class="ml-2">
                  {{ formatDate(item.organization_created_on * 1000) }}
                </div>
              </div>
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-menu>
                <template v-slot:activator="{ props }">
                  <v-btn icon elevation="0" color="transparent" v-bind="props">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list>
                  <v-list-item>
                    <v-tooltip text="Edit">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          elevation="0"
                          icon
                          size="small"
                          @click="openEditDialog(item)"
                          class="action-btn"
                        >
                          <v-icon color="primary">mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </v-list-item>

                  <v-list-item>
                    <v-tooltip text="Team Details">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          elevation="0"
                          class="action-btn"
                          icon
                          size="small"
                          @click="openDetailsDialog(item)"
                        >
                          <v-icon color="pink">mdi-information-outline</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </v-list-item>

                  <v-list-item>
                    <v-tooltip text="Members">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          class="action-btn"
                          elevation="0"
                          icon
                          size="small"
                          @click="openMembersDialog(item)"
                        >
                          <v-icon color="green">mdi-account-group</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </v-list-item>

                  <v-list-item>
                    <v-tooltip text="Audit Logs">
                      <template v-slot:activator="{ props }">
                        <v-btn
                          v-bind="props"
                          class="action-btn"
                          elevation="0"
                          icon
                          size="small"
                          @click="openAuditLogs(item)"
                        >
                          <v-icon color="orange">mdi-file-edit</v-icon>
                        </v-btn>
                      </template>
                    </v-tooltip>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
          <v-pagination
            v-model="page"
            class="v-pagination"
            density="compact"
            :length="pageCount"
            :total-visible="1"
          ></v-pagination>
        </v-card>
      </v-col>
    </v-row>

    <EditTeamDialog
      v-model="editDialog"
      :team="editingTeam"
      @team-updated="onTeamUpdated"
      @update:modelValue="onDialogClose"
    />

    <TeamDetailsDialog v-model="detailsDialog" :team="selectedTeam" />
    <MembersDialog
      :DialogMembers="DialogMembers"
      :organization-id="selectedOrganizationId"
      @clicked="DialogMembersEmit"
    />
    <AuditLogsDialog
      :DialogAuditLogs="DialogAuditLogs"
      :organization-id="selectedOrganizationId"
      @clicked="DialogAuditLogsEmit"
    />
  </v-container>
</template>

<script>
import EditTeamDialog from "@/components/Dialogs/EditTeamDialog.vue";
import TeamDetailsDialog from "@/components/Dialogs/TeamDetailsDialog.vue";
import MembersDialog from "@/components/Dialogs/MembersDialog.vue";
import AuditLogsDialog from "@/components/Dialogs/AuditLogsDialog.vue";
import { generateClient } from "aws-amplify/api";
const client = generateClient();
import { list_partner_organizations } from "@/graphql/queries.js";
import { useDisplay } from "vuetify/lib/framework.mjs";

export default {
  name: "TeamsView",
  components: {
    EditTeamDialog,
    TeamDetailsDialog,
    MembersDialog,
    AuditLogsDialog,
  },
  data() {
    return {
      page: 1,
      currentPage: 1,
      previous_page: 0,
      itemsPerPage: 10,

      tableHeight: 0,

      filterType: "ALL",
      selectedFilter: "ALL",
      selectedYear: "All",
      selectedMonth: "All",

      selectedOrganizationId: null,

      editDialog: false,
      detailsDialog: false,
      DialogMembers: false,
      DialogAuditLogs: false,
      loading: false,
      teamUpdated: false,

      editingTeam: {},
      selectedTeam: {},
      CommonVuetifyObj: {},

      teams: [],
      headers: [
        { title: "Team ID", key: "organization_display_id" },
        { title: "Team Name", key: "organization_name" },
        { title: "Email ID", key: "organization_created_by" },
        { title: "Registered On", key: "organization_created_on" },
        { title: "Status", key: "organization_type" },
        { title: "Actions", key: "actions", sortable: false },
      ],
      statusOptions: [
        { title: "All", value: "ALL" },
        { title: "Live", value: "LIVE" },
        { title: "Prospect", value: "PROSPECT" },
      ],
    };
  },
  mounted() {
    this.CommonVuetifyObj = useDisplay();
    this.getTeams();
  },
  computed: {
    liveTeams() {
      return this.teams.filter((team) => team.organization_type === "LIVE");
    },
    prospectTeams() {
      return this.teams.filter((team) => team.organization_type === "PROSPECT");
    },
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = ["All"];
      for (let i = currentYear; i >= currentYear - 5; i--) {
        years.push(i);
      }
      return years;
    },
    monthOptions() {
      return [
        "All",
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];
    },
    filteredTeams() {
      return this.teams;
    },
    pageCount() {
      if (this.filteredTeams.length === 0) return 1;
      return Math.ceil(this.filteredTeams.length / this.itemsPerPage);
    },
  },
  watch: {
    selectedFilter(val) {
      if (val) {
        return this.getTeams(val);
      }
    },
    page(val) {
      if (val > this.previous_page) {
        this.getTeams();
      }
      if (val > this.previous_page) {
        this.previous_page = val;
      }
    },
  },
  methods: {
    async getTeams() {
      this.loading = true;
      try {
        let monthNumber = null;
        if (this.selectedMonth !== "All") {
          const monthMap = {
            January: 1,
            February: 2,
            March: 3,
            April: 4,
            May: 5,
            June: 6,
            July: 7,
            August: 8,
            September: 9,
            October: 10,
            November: 11,
            December: 12,
          };
          monthNumber = monthMap[this.selectedMonth];
        }
        const result = await client.graphql({
          query: list_partner_organizations,
          variables: {
            input: {
              organization_type: this.selectedFilter,
              year: this.selectedYear === "All" ? undefined : this.selectedYear,
              month: monthNumber === undefined ? undefined : monthNumber,
              Limit: 100,
              nextToken: null,
            },
          },
        });
        let response = JSON.parse(result.data.list_partner_organizations);

        if (response.Status === "SUCCESS") {
          this.teams = response.data;
        } else {
          this.teams = [];
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
        this.teams = [];
        this.$store.dispatch("setNotification", {
          type: "error",
          message: "Failed to fetch teams. Please try again.",
        });
      } finally {
        this.loading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    openEditDialog(team) {
      this.editingTeam = { ...team };
      this.editDialog = true;
    },

    openDetailsDialog(team) {
      this.selectedTeam = { ...team };
      this.detailsDialog = true;
    },

    openMembersDialog(item) {
      this.selectedOrganizationId = item.organization_id;
      this.DialogMembers = true;
    },
    openAuditLogs(item) {
      this.selectedOrganizationId = item.organization_id;
      this.DialogAuditLogs = true;
    },
    onTeamUpdated(updatedTeam) {
      this.teamUpdated = true;
      console.log(
        "Team updated:",
        updatedTeam,
        "teamUpdated flag set to:",
        this.teamUpdated
      );
    },
    onDialogClose(isOpen) {
      console.log(
        "Dialog close event:",
        isOpen,
        "teamUpdated:",
        this.teamUpdated
      );
      if (!isOpen && this.teamUpdated) {
        this.getTeams();
        this.teamUpdated = false;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
      this.getTeams();
    },
    onMonthChange() {
      if (this.selectedMonth !== "All" && this.selectedYear === "All") {
        this.selectedYear = new Date().getFullYear();
      }
      this.currentPage = 1;
      this.getTeams();
    },
    DialogMembersEmit() {
      this.DialogMembers = false;
    },
    DialogAuditLogsEmit() {
      this.DialogAuditLogs = false;
    },
  },
};
</script>

<style scoped>
.table-card {
  border-radius: 12px;
  overflow: hidden;
}

.modern-data-table {
  border-radius: 12px;
  overflow: hidden;
}

.modern-data-table :deep(.v-data-table-header) {
  background-color: #f8f9fa;
}

.modern-data-table :deep(.v-data-table-header th) {
  background-color: #f8f9fa !important;
  font-weight: 600;
  font-size: 0.875rem;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px !important;
}

.modern-data-table :deep(.v-data-table__tr) {
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease;
}

.modern-data-table :deep(.v-data-table__tr:hover) {
  background-color: #f8f9fa;
}

.modern-data-table :deep(.v-data-table__td) {
  font-size: 0.875rem;
  color: #212529;
  vertical-align: middle;
  padding: 12px 16px !important;
}

.status-chip {
  font-weight: 600;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
  border-radius: 6px;
  min-width: 80px;
  text-align: center;
  justify-content: center;
}

.action-btn {
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background-color: rgba(25, 118, 210, 0.1);
  transform: scale(1.1);
}

.filter-container {
  gap: 16px;
}

.filter-select {
  min-width: 120px;
  max-width: 140px;
}

.filter-select :deep(.v-field) {
  border-radius: 8px;
  font-size: 0.875rem;
}

.filter-select :deep(.v-field__input) {
  font-size: 0.875rem;
  font-weight: 500;
}

.v-chip-group {
  margin-left: 8px;
}

.v-chip-group .v-chip {
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.v-chip-group .v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.v-card {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  margin: 16px;
}

.compact-header {
  margin-bottom: 8px;
}

.v-card-title {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 16px 20px;
}

.v-card-title h2 {
  color: #212529;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 1.5rem;
}

.v-card-title p {
  color: #6c757d;
  margin: 0;
  font-size: 0.875rem;
}

.v-container {
  background-color: #f0f1f2 !important;
  padding: 16px;
}

.v-row {
  margin: 0;
}

.v-col {
  padding: 4px;
}

.v-pagination--is-active {
  background: linear-gradient(135deg, #3f78e0 0%, #da4c77 100%) !important;
  color: white !important;
}

.icon-color {
  background: linear-gradient(135deg, #1967c0 0%, #0f3d73 100%) !important;
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
}
</style>
